import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  Edit,
  Save,
  Plus,
  Trash2,
  FileText,
  Image as ImageIcon,
  Download,
  Ban,
  CalendarClock,
  ExternalLink,
  AlertCircle,
} from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Modal } from '../../components/ui/Modal';
import { ImageUpload } from '../../components/ui/ImageUpload';
import { Toast } from '../../components/ui/Toast';
import { api, getPropertyDocumentDownloadUrl, getPropertyCoverImageUrl } from '../../services/api';
import { usePermissions } from '../../hooks/usePermissions';
import { Tenant, TenantDeliveryHistoryEntry, Unit } from '../../types';
import { formatCurrencyInput, parseCurrencyToNumber } from '../../utils/validation';
import {
  buildSolicitudFormDisplayState,
  formatDateSpanishLong,
  formatDateTimeSpanishLong,
  formatDatoForField,
  formatPhoneDisplay,
  getDocDisplayName,
  getDocDownloadUrl,
  getDocMime,
  isDocImage,
  isDocPdf,
  labelizeKey,
} from '../../utils/solicitudFormDisplay';
import { isPublicSolicitudContestada } from '../../utils/solicitudNotes';
import { DocumentViewer } from '../../components/documents/DocumentViewer';
import { formatDateLong, formatDateTime } from '../../utils/date';
import { TenantPaymentsTab } from './TenantPaymentsTab';
import { DeliveryInspectionSection } from '../../components/tenants/DeliveryInspectionSection';
import { DeliveryInspectionResults } from '../../components/tenants/DeliveryInspectionResults';
import { ExpireContractButton } from '../../components/tenants/ExpireContractButton';
import { HistoryTable } from '../../components/history/HistoryTable';
import { mapTimelineToChangeLogEntries, mergeHistoryRows } from '../../utils/historyRows';

function mapApiTenantToTenant(item: any): Tenant {
  const startDate = item.startDate
    ? (typeof item.startDate === 'string' ? item.startDate : new Date(item.startDate).toISOString().split('T')[0])
    : '';
  const endDate = item.endDate
    ? (typeof item.endDate === 'string' ? item.endDate : new Date(item.endDate).toISOString().split('T')[0])
    : '';
  const ccd = item.contractCancellationDate ?? item.contract_cancellation_date;
  const contractCancellationDate =
    ccd != null
      ? typeof ccd === 'string'
        ? ccd.split('T')[0]
        : new Date(ccd).toISOString().split('T')[0]
      : undefined;
  return {
    id: item.id,
    name: item.name,
    email: item.email,
    phone: item.phone,
    propertyId: item.propertyId,
    unitNumber: item.unitNumber,
    monthlyRent: Number(item.monthlyRent) || 0,
    startDate,
    endDate,
    status: item.status || 'active',
    contract: item.contract,
    idOficial: item.idOficial,
    contractCancellationDate,
    contractCancellationReason: item.contractCancellationReason ?? item.contract_cancellation_reason,
    contractCancellationPenaltyAmount:
      item.contractCancellationPenaltyAmount != null || item.contract_cancellation_penalty_amount != null
        ? Number(item.contractCancellationPenaltyAmount ?? item.contract_cancellation_penalty_amount)
        : null,
    contractCancellationPenaltyWaivedInternal:
      item.contractCancellationPenaltyWaivedInternal ?? item.contract_cancellation_penalty_waived_internal ?? false,
    deliveryChecklistDone: item.deliveryChecklistDone ?? item.delivery_checklist_done ?? false,
    deliverySigned: item.deliverySigned ?? item.delivery_signed ?? false,
    deliveryPaid: item.deliveryPaid ?? item.delivery_paid ?? false,
    deliveryHistory: mapDeliveryHistory(item.tenantDeliveryHistory ?? item.tenant_delivery_history),
    prospectId: item.prospectId ?? null,
    accountUser: item.accountUser ?? null,
    assignedManager: item.assignedManager ?? null,
  };
}

function mapDeliveryHistory(raw: unknown): TenantDeliveryHistoryEntry[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((h: any) => ({
    id: h.id,
    createdAt:
      typeof h.createdAt === 'string'
        ? h.createdAt
        : h.created_at
        ? new Date(h.created_at).toISOString()
        : '',
    checklistDone: h.checklistDone ?? h.checklist_done ?? false,
    signed: h.signed ?? false,
    paid: h.paid ?? false,
    notes: h.notes ?? null,
  }));
}

/** Suma días a una fecha YYYY-MM-DD en calendario local (evita desfases UTC). */
function addDaysToIsoDate(ymd: string, days: number): string {
  const [y, m, day] = ymd.split('-').map(Number);
  const d = new Date(y, m - 1, day + days);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export const TenantDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [unit, setUnit] = useState<Unit | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [monthlyRentDisplay, setMonthlyRentDisplay] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [manualChecklistOpen, setManualChecklistOpen] = useState(false);
  const [manualChecklistSubmitting, setManualChecklistSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    contract: '',
    startDate: '',
    endDate: '',
    monthlyRent: 0,
    idOficial: '',
    status: 'active' as 'active' | 'pending' | 'expired' | 'terminado' | 'cancelado',
  });

  const [propertyName, setPropertyName] = useState<string | null>(null);
  const [propertyCoverImage, setPropertyCoverImage] = useState<string | null>(null);
  const [tenantDocuments, setTenantDocuments] = useState<{ id: string; title: string; fileUrl?: string; fileType?: string }[]>([]);
  /** JSON de formulario público en application.notes (mismos adjuntos que en la solicitud). */
  const [solicitudNotes, setSolicitudNotes] = useState<string | null>(null);
  const [solicitudApplicationId, setSolicitudApplicationId] = useState<string | null>(null);
  const [newDocument, setNewDocument] = useState({ title: '', fileUrl: '', fileType: '' });
  const [documentUploading, setDocumentUploading] = useState(false);
  const [documentUploadProgress, setDocumentUploadProgress] = useState(0);
  const documentProgressIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [activeTab, setActiveTab] = useState<
    'general' | 'informacion' | 'documents' | 'history' | 'property' | 'payments' | 'delivery-checklist'
  >('general');

  const { hasPermission } = usePermissions();
  const canViewPrices = hasPermission('view_prices');
  const canUpdateRentals = hasPermission('update_rentals') || hasPermission('manage_rentals');
  const canManualCompleteChecklist = canUpdateRentals;
  const canDeleteRentals = hasPermission('delete_rentals');
  const canRenewContract = hasPermission('renew_tenant_contract');

  const [portalAccountLoading, setPortalAccountLoading] = useState(false);
  const [portalAccountMessage, setPortalAccountMessage] = useState<string | null>(null);
  const [assignManagerLoading, setAssignManagerLoading] = useState(false);
  const [assignManagerId, setAssignManagerId] = useState<string>('');

  const [isRenewModalOpen, setIsRenewModalOpen] = useState(false);
  const [renewSubmitting, setRenewSubmitting] = useState(false);
  const [renewForm, setRenewForm] = useState({ newEndDate: '', newMonthlyRentDisplay: '' });

  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [cancelSubmitting, setCancelSubmitting] = useState(false);
  const [cancelForm, setCancelForm] = useState({
    confirm: false,
    cancellationDate: new Date().toISOString().split('T')[0],
    reason: '',
    penaltyWaivedInternal: false,
    penaltyAmountDisplay: '',
    assignedUserId: '',
  });

  const [isRevertModalOpen, setIsRevertModalOpen] = useState(false);
  const [revertSubmitting, setRevertSubmitting] = useState(false);
  const [revertError, setRevertError] = useState<string | null>(null);
  const [releaseUnitLoading, setReleaseUnitLoading] = useState(false);

  const { data: maintenanceUsers = [] } = useQuery({
    queryKey: ['maintenance-or-admin-users'],
    queryFn: () => api.getMaintenanceOrAdminUsers(),
    enabled: isCancelModalOpen,
  });

  const { data: adminUsersRes } = useQuery({
    queryKey: ['users', 'list', 'admin'],
    queryFn: () => api.getUsers({ limit: 200 }),
    staleTime: 120_000,
    enabled: canUpdateRentals,
  });
  const adminUsers: { id: string; name: string; email: string }[] = adminUsersRes?.data ?? [];

  const { data: tenantTimeline = [], isLoading: tenantTimelineLoading } = useQuery({
    queryKey: ['tenant-timeline', id],
    queryFn: () => api.getTenantTimeline(id!),
    enabled: !!id,
    staleTime: 30_000,
  });
  const { data: tenantChangeLogsPersisted = [], isLoading: tenantChangeLogsLoading } = useQuery({
    queryKey: ['change-logs', 'TENANT', id],
    queryFn: () => api.getChangeLogs({ entityId: id!, entityType: 'TENANT', limit: 200 }),
    enabled: !!id,
    staleTime: 30_000,
  });
  const historyRows = useMemo(() => {
    if (!id) return [];
    return mergeHistoryRows(
      mapTimelineToChangeLogEntries(id, 'TENANT', tenantTimeline),
      tenantChangeLogsPersisted,
    );
  }, [id, tenantTimeline, tenantChangeLogsPersisted]);
  const historyLoading = tenantTimelineLoading || tenantChangeLogsLoading;

  useEffect(() => {
    return () => {
      if (documentProgressIntervalRef.current) {
        clearInterval(documentProgressIntervalRef.current);
        documentProgressIntervalRef.current = null;
      }
    };
  }, []);

  const calculateContractDuration = (startDate: string, endDate: string) => {
    if (!startDate || !endDate) return 0;
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.round(diffDays / 30);
  };

  const loadData = useCallback(async () => {
    if (!id) return;
    try {
      setError(null);
      setLoading(true);
      const tenantData = await api.getTenant(id);
      setTenant(mapApiTenantToTenant(tenantData));
      setPropertyName(tenantData.properties?.name ?? null);
      setPropertyCoverImage(tenantData.properties?.coverImage ?? tenantData.properties?.cover_image ?? null);
      setTenantDocuments(
        Array.isArray(tenantData.tenant_documents)
          ? tenantData.tenant_documents.map((d: any) => ({
              id: d.id,
              title: d.title,
              fileUrl: d.fileUrl ?? d.file_url,
              fileType: d.fileType ?? d.file_type,
            }))
          : []
      );
      const unitsRes = await api.getUnits(tenantData.propertyId);
      const unitList: any[] = Array.isArray(unitsRes)
        ? unitsRes
        : Array.isArray((unitsRes as any)?.data)
        ? (unitsRes as any).data
        : [];
      const u = unitList.find(
        (u: any) => u.propertyId === tenantData.propertyId && u.unitNumber === tenantData.unitNumber
      );
      setUnit(
        u
          ? {
              id: u.id,
              propertyId: u.propertyId,
              unitNumber: u.unitNumber,
              m2: u.m2,
              bedrooms: u.bedrooms,
              bathrooms: u.bathrooms,
              status: u.status || 'available',
              tenantId: u.tenantId,
              monthlyRent: u.monthlyRent,
              contractStartDate: u.contractStartDate,
              contractEndDate: u.contractEndDate,
              notes: u.notes,
            }
          : null
      );

      let nextSolicitudNotes: string | null = null;
      let nextSolicitudAppId: string | null = null;
      const prospectId = tenantData.prospectId ?? tenantData.prospect_id ?? null;
      if (prospectId) {
        try {
          const appsRes = await api.getApplications({ limit: 500 });
          const appsList = appsRes?.data ?? [];
          const match =
            appsList.find(
              (a: any) =>
                String(a.prospectId) === String(prospectId) &&
                a.propertyId === tenantData.propertyId &&
                a.unitNumber === tenantData.unitNumber,
            ) ?? appsList.find((a: any) => String(a.prospectId) === String(prospectId));
          if (match?.id) {
            const fullApp = await api.getApplication(match.id);
            nextSolicitudNotes = typeof fullApp?.notes === 'string' ? fullApp.notes : null;
            nextSolicitudAppId = fullApp?.id ?? null;
          }
        } catch {
          /* no bloquear la ficha del inquilino */
        }
      }
      setSolicitudNotes(nextSolicitudNotes);
      setSolicitudApplicationId(nextSolicitudAppId);
    } catch (err: any) {
      setError(err.message || 'Error al cargar el inquilino');
      setTenant(null);
      setUnit(null);
      setSolicitudNotes(null);
      setSolicitudApplicationId(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const {
    parsedNotes: solicitudParsedNotes,
    datosFormulario: solicitudDatosFormulario,
    orderedKeys: solicitudOrderedKeys,
    docKeysWithValue: solicitudDocKeysWithValue,
  } = useMemo(() => buildSolicitudFormDisplayState(solicitudNotes), [solicitudNotes]);

  const solicitudFormularioContestada = isPublicSolicitudContestada(solicitudNotes);

  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get('tab') !== 'pagos') return;
    setActiveTab('payments');
    const next = new URLSearchParams(searchParams);
    next.delete('tab');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (searchParams.get('focus') !== 'cancel') return;
    if (!tenant || !id || tenant.id !== id) return;
    const next = new URLSearchParams(searchParams);
    next.delete('focus');
    setSearchParams(next, { replace: true });
    if (tenant.status === 'cancelado' || !canUpdateRentals) return;
    setCancelForm({
      confirm: false,
      cancellationDate: new Date().toISOString().split('T')[0],
      reason: '',
      penaltyWaivedInternal: false,
      penaltyAmountDisplay: '',
      assignedUserId: '',
    });
    setIsCancelModalOpen(true);
  }, [tenant, id, searchParams, canUpdateRentals, setSearchParams]);

  if (loading && !tenant) {
    return (
      <div className="flex justify-center py-12 text-gray-500">Cargando inquilino...</div>
    );
  }

  if (error && !tenant) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">{error}</p>
        <Button onClick={() => navigate('/rentas/inquilinos')} className="mt-4">
          Volver a la lista
        </Button>
      </div>
    );
  }

  if (!tenant) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Inquilino no encontrado</p>
        <Button onClick={() => navigate('/rentas/inquilinos')} className="mt-4">
          Volver a la lista
        </Button>
      </div>
    );
  }

  const handleOpenEditModal = () => {
    setFormData({
      contract: tenant.contract || '',
      startDate: tenant.startDate,
      endDate: tenant.endDate,
      monthlyRent: tenant.monthlyRent,
      idOficial: tenant.idOficial || '',
      status: tenant.status,
    });
    setMonthlyRentDisplay(tenant.monthlyRent > 0 ? String(tenant.monthlyRent) : '');
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
  };

  const handleSave = async () => {
    try {
      setIsSubmitting(true);
      await api.updateTenant(tenant.id, {
        startDate: formData.startDate,
        endDate: formData.endDate,
        monthlyRent: formData.monthlyRent,
        status: formData.status,
        contract: formData.contract || undefined,
        idOficial: formData.idOficial || undefined,
      });
      setToast({ message: 'Cambios guardados correctamente', type: 'success' });
      handleCloseEditModal();
      await loadData();
      await queryClient.invalidateQueries({ queryKey: ['tenant-timeline', id] });
      await queryClient.invalidateQueries({ queryKey: ['change-logs', 'TENANT', id] });
    } catch (err: any) {
      setToast({ message: err.message || 'Error al guardar', type: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreatePortalAccount = async () => {
    if (!tenant) return;
    try {
      setPortalAccountLoading(true);
      setPortalAccountMessage(null);
      await api.createTenantAccount(tenant.id, { email: tenant.email });
      setPortalAccountMessage('Cuenta del portal creada. El inquilino recibirá un correo con sus credenciales.');
      await loadData();
    } catch (err: any) {
      setPortalAccountMessage(err.message || 'Error al crear la cuenta del portal');
    } finally {
      setPortalAccountLoading(false);
    }
  };

  const handleAssignManager = async () => {
    if (!tenant) return;
    try {
      setAssignManagerLoading(true);
      await api.assignTenantManager(tenant.id, assignManagerId || null);
      setToast({ message: 'Administrador asignado correctamente', type: 'success' });
      await loadData();
    } catch (err: any) {
      setToast({ message: err.message || 'Error al asignar administrador', type: 'error' });
    } finally {
      setAssignManagerLoading(false);
    }
  };

  const handleDocumentFileChange = (fileUrl: string, fileType?: string) => {
    setNewDocument((prev) => ({ ...prev, fileUrl, fileType: fileType || 'application/pdf' }));
  };

  const handleDocumentFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setDocumentUploading(true);
    setDocumentUploadProgress(0);
    if (documentProgressIntervalRef.current) clearInterval(documentProgressIntervalRef.current);
    documentProgressIntervalRef.current = setInterval(() => {
      setDocumentUploadProgress((p) => {
        if (p >= 90) {
          if (documentProgressIntervalRef.current) {
            clearInterval(documentProgressIntervalRef.current);
            documentProgressIntervalRef.current = null;
          }
          return 90;
        }
        return p + 4;
      });
    }, 120);
    const reader = new FileReader();
    reader.onloadend = () => {
      if (documentProgressIntervalRef.current) {
        clearInterval(documentProgressIntervalRef.current);
        documentProgressIntervalRef.current = null;
      }
      setDocumentUploadProgress(100);
      const fileType = file.type === 'application/pdf' ? 'pdf' : file.type.split('/')[1] || 'pdf';
      handleDocumentFileChange(reader.result as string, fileType);
      setTimeout(() => {
        setDocumentUploading(false);
        setDocumentUploadProgress(0);
      }, 400);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleAddDocument = async () => {
    if (!tenant?.id || !newDocument.title.trim() || !newDocument.fileUrl) return;
    try {
      const doc = await api.addTenantDocument(tenant.id, {
        title: newDocument.title.trim(),
        fileUrl: newDocument.fileUrl,
        fileType: newDocument.fileType || undefined,
      });
      setTenantDocuments((prev) => [...prev, { id: doc.id, title: doc.title, fileUrl: doc.fileUrl, fileType: doc.fileType }]);
      setNewDocument({ title: '', fileUrl: '', fileType: '' });
      setToast({ message: 'Documento agregado', type: 'success' });
      await queryClient.invalidateQueries({ queryKey: ['tenant-timeline', tenant.id] });
      await queryClient.invalidateQueries({ queryKey: ['change-logs', 'TENANT', tenant.id] });
    } catch (err: any) {
      setToast({ message: err.message || 'Error al agregar documento', type: 'error' });
    }
  };

  const handleRemoveDocument = async (documentId: string) => {
    if (!id || !tenant?.id) return;
    try {
      await api.deleteTenantDocument(documentId);
      setTenantDocuments((prev) => prev.filter((d) => d.id !== documentId));
      setToast({ message: 'Documento eliminado', type: 'success' });
      await queryClient.invalidateQueries({ queryKey: ['tenant-timeline', id] });
      await queryClient.invalidateQueries({ queryKey: ['change-logs', 'TENANT', id] });
    } catch (err: any) {
      setToast({ message: err.message || 'Error al eliminar documento', type: 'error' });
    }
  };

  const openCancelModal = () => {
    setCancelForm({
      confirm: false,
      cancellationDate: new Date().toISOString().split('T')[0],
      reason: '',
      penaltyWaivedInternal: false,
      penaltyAmountDisplay: '',
      assignedUserId: '',
    });
    setIsCancelModalOpen(true);
  };

  const openRenewModal = () => {
    if (!tenant) return;
    const defaultEnd = addDaysToIsoDate(tenant.endDate, 365);
    setRenewForm({
      newEndDate: defaultEnd,
      newMonthlyRentDisplay: canViewPrices
        ? tenant.monthlyRent.toLocaleString('es-MX', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
        : '',
    });
    setIsRenewModalOpen(true);
  };

  const handleRenewContractSubmit = async () => {
    if (!tenant) return;
    const newEndStr = renewForm.newEndDate.split('T')[0];
    const currentEndStr = tenant.endDate.split('T')[0];
    if (!newEndStr || newEndStr <= currentEndStr) {
      setToast({ message: 'La nueva fecha de fin debe ser posterior a la fecha de fin actual.', type: 'error' });
      return;
    }
    const body: { newEndDate: string; newMonthlyRent?: number } = { newEndDate: newEndStr };
    if (canViewPrices && renewForm.newMonthlyRentDisplay.trim()) {
      const n = parseCurrencyToNumber(formatCurrencyInput(renewForm.newMonthlyRentDisplay));
      if (!Number.isFinite(n) || n < 0) {
        setToast({ message: 'La renta mensual no es válida.', type: 'error' });
        return;
      }
      if (n !== tenant.monthlyRent) {
        body.newMonthlyRent = n;
      }
    }
    try {
      setRenewSubmitting(true);
      await api.renewTenantContract(tenant.id, body);
      setToast({ message: 'Contrato renovado correctamente.', type: 'success' });
      setIsRenewModalOpen(false);
      await loadData();
    } catch (err: any) {
      setToast({ message: err.message || 'Error al renovar el contrato', type: 'error' });
    } finally {
      setRenewSubmitting(false);
    }
  };

  const handleCancelContractSubmit = async () => {
    if (!tenant) return;
    if (!cancelForm.confirm) {
      setToast({ message: 'Confirma que estás seguro antes de cancelar el contrato.', type: 'error' });
      return;
    }
    const reason = cancelForm.reason.trim();
    if (!reason) {
      setToast({ message: 'La razón de cancelación es obligatoria.', type: 'error' });
      return;
    }
    if (!cancelForm.penaltyWaivedInternal) {
      if (!cancelForm.penaltyAmountDisplay.trim()) {
        setToast({ message: 'Indica el monto de penalidad cobrada o marca sin penalidad por razones internas.', type: 'error' });
        return;
      }
      const amt = parseCurrencyToNumber(formatCurrencyInput(cancelForm.penaltyAmountDisplay));
      if (!Number.isFinite(amt) || amt < 0) {
        setToast({ message: 'Indica un monto de penalidad válido (o marca sin penalidad por razones internas).', type: 'error' });
        return;
      }
    }
    try {
      setCancelSubmitting(true);
      await api.cancelTenantContract(tenant.id, {
        cancellationDate: cancelForm.cancellationDate,
        reason,
        penaltyWaivedInternal: cancelForm.penaltyWaivedInternal,
        ...(!cancelForm.penaltyWaivedInternal
          ? { penaltyAmount: parseCurrencyToNumber(formatCurrencyInput(cancelForm.penaltyAmountDisplay)) }
          : {}),
        ...(cancelForm.assignedUserId.trim() ? { assignedUserId: cancelForm.assignedUserId.trim() } : {}),
      });
      setToast({ message: 'Contrato cancelado correctamente.', type: 'success' });
      setIsCancelModalOpen(false);
      await loadData();
    } catch (err: any) {
      setToast({ message: err.message || 'Error al cancelar el contrato', type: 'error' });
    } finally {
      setCancelSubmitting(false);
    }
  };

  const openRevertModal = () => {
    setRevertError(null);
    setIsRevertModalOpen(true);
  };

  const handleRevertConfirm = async () => {
    if (!tenant) return;
    try {
      setRevertSubmitting(true);
      setRevertError(null);
      await api.revertTenantToProspect(tenant.id);
      const hadProspect = !!tenant.prospectId;
      setIsRevertModalOpen(false);
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
      queryClient.invalidateQueries({ queryKey: ['prospects'] });
      setToast({
        message: hadProspect
          ? `"${tenant.name}" volvió a la lista de prospectos.`
          : `Se eliminaron los datos del inquilino "${tenant.name}".`,
        type: 'success',
      });
      navigate('/rentas/inquilinos');
    } catch (err: any) {
      setRevertError(err?.message || 'No se pudo completar la operación');
    } finally {
      setRevertSubmitting(false);
    }
  };

  const contractDuration = calculateContractDuration(tenant.startDate, tenant.endDate);
  const formContractDuration = calculateContractDuration(formData.startDate, formData.endDate);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button variant="ghost" icon={ArrowLeft} onClick={() => navigate('/rentas/inquilinos')}>
            Volver
          </Button>
          <h1 className="text-2xl font-bold text-gray-900">{tenant.name}</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <span className="px-3 py-1 rounded-full text-sm font-medium bg-primary/10 text-primary border border-primary/20">
            Inquilino
          </span>
          {canUpdateRentals && canDeleteRentals && (
            <button
              type="button"
              onClick={openRevertModal}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-amber-50 text-amber-900 border border-amber-200 hover:bg-amber-100 transition-colors"
              title={tenant.prospectId ? 'Regresar a prospecto' : 'Eliminar inquilino y datos asociados'}
            >
              <ArrowLeft className="w-4 h-4 shrink-0" />
              Regresar a prospecto
            </button>
          )}
          {canUpdateRentals &&
            (tenant.status === 'cancelado' || tenant.status === 'expired' || tenant.status === 'terminado') &&
            unit?.status === 'occupied' && (
              <button
                type="button"
                disabled={releaseUnitLoading}
                onClick={async () => {
                  if (!window.confirm('¿Liberar la unidad y marcarla como disponible?')) return;
                  setReleaseUnitLoading(true);
                  try {
                    await api.releaseStuckUnit(tenant.id);
                    setUnit((u) => u ? { ...u, status: 'available', tenantId: undefined } : u);
                    setToast({ message: 'Unidad liberada correctamente', type: 'success' });
                  } catch (e: any) {
                    setToast({ message: e.message || 'No se pudo liberar la unidad', type: 'error' });
                  } finally {
                    setReleaseUnitLoading(false);
                  }
                }}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-red-50 text-red-800 border border-red-200 hover:bg-red-100 transition-colors disabled:opacity-50"
                title="La unidad figura como ocupada aunque el contrato está cancelado"
              >
                {releaseUnitLoading ? (
                  <span className="w-4 h-4 border-2 border-red-400 border-t-transparent rounded-full animate-spin" />
                ) : (
                  <AlertCircle className="w-4 h-4 shrink-0" />
                )}
                Liberar unidad
              </button>
            )}
          {canUpdateRentals && (
            <Button icon={Edit} onClick={handleOpenEditModal}>
              Editar
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 bg-white rounded-t-lg">
        <nav className="flex space-x-8">
          {[
            { id: 'general', label: 'General' },
            { id: 'informacion', label: 'Información' },
            { id: 'documents', label: 'Documentos' },
            { id: 'payments', label: 'Pagos' },
            { id: 'delivery-checklist', label: 'Checklist de entrega' },
            { id: 'history', label: 'Historial' },
            { id: 'property', label: 'Propiedad' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.id
                  ? 'border-accent text-accent'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab: General */}
      {activeTab === 'general' && (
        <div className="space-y-6 bg-white rounded-b-lg border border-t-0 border-gray-200 p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card title="Información del Inquilino">
              <div className="space-y-4">
                <div>
                  <label className="text-sm text-gray-600">Nombre</label>
                  <p className="text-base font-medium">{tenant.name}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-600">Email</label>
                  <p className="text-base font-medium">{tenant.email}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-600">Teléfono</label>
                  <p className="text-base font-medium">
                    {tenant.phone ? formatPhoneDisplay(String(tenant.phone)) : '—'}
                  </p>
                </div>
                <div>
                  <label className="text-sm text-gray-600">Estado</label>
                  <p className="text-base font-medium">
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        tenant.status === 'active'
                          ? 'bg-green-100 text-green-800'
                          : tenant.status === 'pending'
                          ? 'bg-yellow-100 text-yellow-800'
                          : tenant.status === 'expired'
                          ? 'bg-red-100 text-red-800'
                          : tenant.status === 'terminado'
                          ? 'bg-blue-100 text-blue-800'
                          : tenant.status === 'cancelado'
                          ? 'bg-gray-100 text-gray-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {tenant.status === 'active'
                        ? 'Activo'
                        : tenant.status === 'pending'
                        ? 'Pendiente'
                        : tenant.status === 'expired'
                        ? 'Expirado'
                        : tenant.status === 'terminado'
                        ? 'Terminado'
                        : tenant.status === 'cancelado'
                        ? 'Cancelado'
                        : tenant.status}
                    </span>
                  </p>
                </div>
              </div>
            </Card>

            <Card title="Información del Contrato">
              <div className="space-y-4">
                <div>
                  <label className="text-sm text-gray-600">Valor del Contrato (Renta Mensual)</label>
                  <p className="text-2xl font-bold text-primary">
                    {canViewPrices ? `$${tenant.monthlyRent.toLocaleString('es-MX')}` : '—'}
                  </p>
                </div>
                <div>
                  <label className="text-sm text-gray-600">Duración del Contrato</label>
                  <p className="text-base font-medium">
                    {contractDuration} {contractDuration === 1 ? 'mes' : 'meses'}
                  </p>
                </div>
                <div>
                  <label className="text-sm text-gray-600">Fecha de Inicio</label>
                  <p className="text-base font-medium">{formatDateLong(tenant.startDate)}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-600">Fecha de Fin</label>
                  <p className="text-base font-medium">{formatDateLong(tenant.endDate)}</p>
                </div>
              </div>
            </Card>
          </div>

          {/* Checklist de entrega — Tab General: solo indicadores de progreso */}
          <Card>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Checklist de entrega</h3>
              {tenant.deliveryPaid && tenant.deliverySigned && tenant.deliveryChecklistDone ? (
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">Completado</span>
              ) : (
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">Pendiente</span>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div
                className={`p-4 rounded-lg border-2 ${
                  tenant.deliveryPaid ? 'border-green-300 bg-green-50' : 'border-gray-200 bg-gray-50'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                      tenant.deliveryPaid ? 'bg-green-500 text-white' : 'bg-gray-300 text-gray-600'
                    }`}
                  >
                    {tenant.deliveryPaid ? '✓' : '1'}
                  </div>
                  <div>
                    <p className="font-medium text-sm">Pagado</p>
                    <p className="text-xs text-gray-500">Depósito registrado en Pagos</p>
                  </div>
                </div>
              </div>

              <div
                className={`p-4 rounded-lg border-2 ${
                  tenant.deliverySigned ? 'border-green-300 bg-green-50' : 'border-gray-200 bg-gray-50'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                      tenant.deliverySigned ? 'bg-green-500 text-white' : 'bg-gray-300 text-gray-600'
                    }`}
                  >
                    {tenant.deliverySigned ? '✓' : '2'}
                  </div>
                  <div>
                    <p className="font-medium text-sm">Firmado</p>
                    <p className="text-xs text-gray-500">Contrato subido en Documentos</p>
                  </div>
                </div>
              </div>

              <div
                className={`p-4 rounded-lg border-2 ${
                  tenant.deliveryChecklistDone ? 'border-green-300 bg-green-50' : 'border-gray-200 bg-gray-50'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                      tenant.deliveryChecklistDone ? 'bg-green-500 text-white' : 'bg-gray-300 text-gray-600'
                    }`}
                  >
                    {tenant.deliveryChecklistDone ? '✓' : '3'}
                  </div>
                  <div>
                    <p className="font-medium text-sm">Inspección</p>
                    <p className="text-xs text-gray-500">Checklist completado por técnico</p>
                  </div>
                </div>
              </div>
            </div>
          </Card>

          <Card title="Renovación de contrato">
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Extiende la vigencia del contrato y, si aplica, actualiza la renta mensual. La unidad ocupada se
                sincroniza con la nueva fecha de fin.
              </p>
              {tenant.status === 'cancelado' ? (
                <p className="text-sm text-gray-500">No aplica en contratos cancelados.</p>
              ) : canRenewContract ? (
                <Button type="button" variant="secondary" icon={CalendarClock} onClick={openRenewModal}>
                  Renovar contrato
                </Button>
              ) : (
                <p className="text-sm text-gray-500">
                  Solo perfiles con permiso de renovación (p. ej. Administración o Legal) pueden registrar este cambio.
                </p>
              )}
            </div>
          </Card>

          <Card title="Vencimiento del contrato">
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Usa esta opción cuando el contrato haya llegado a su fin sin cancelación anticipada. El inquilino pasa a
                estado vencido y la unidad queda disponible.
              </p>
              {tenant.status === 'cancelado' ? (
                <p className="text-sm text-gray-500">No aplica en contratos cancelados.</p>
              ) : tenant.status === 'expired' ? (
                <p className="text-sm text-gray-500">Este contrato ya está marcado como vencido.</p>
              ) : canUpdateRentals ? (
                <ExpireContractButton tenantId={tenant.id} status={tenant.status} canUpdate onSuccess={loadData} />
              ) : (
                <p className="text-sm text-gray-500">Solo quienes pueden editar rentas pueden registrar el vencimiento.</p>
              )}
            </div>
          </Card>

          <Card title="Cancelación de contrato">
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Registra la baja del contrato: fecha, razón y monto de penalidad cobrada, o indica si no hubo cobro por
                razones internas. La unidad asociada quedará disponible. Opcionalmente asigna un técnico para inspección de
                salida.
              </p>
              {tenant.status === 'cancelado' && (tenant.contractCancellationDate || tenant.contractCancellationReason) ? (
                <div className="space-y-3 text-sm">
                  {tenant.contractCancellationDate && (
                    <div>
                      <span className="text-gray-500">Fecha de cancelación: </span>
                      <span className="font-medium text-gray-900">
                        {formatDateLong(tenant.contractCancellationDate)}
                      </span>
                    </div>
                  )}
                  {tenant.contractCancellationReason && (
                    <div>
                      <span className="text-gray-500 block mb-1">Razón</span>
                      <p className="text-gray-900 whitespace-pre-wrap">{tenant.contractCancellationReason}</p>
                    </div>
                  )}
                  <div>
                    <span className="text-gray-500">Penalidad: </span>
                    {tenant.contractCancellationPenaltyWaivedInternal ? (
                      <span className="font-medium text-gray-900">Sin penalidad (razones internas)</span>
                    ) : (
                      <span className="font-medium text-gray-900">
                        {canViewPrices && tenant.contractCancellationPenaltyAmount != null
                          ? `$${Number(tenant.contractCancellationPenaltyAmount).toLocaleString('es-MX')}`
                          : 'Penalidad registrada'}
                      </span>
                    )}
                  </div>
                </div>
              ) : tenant.status === 'cancelado' ? (
                <p className="text-sm text-gray-500">Este contrato está marcado como cancelado.</p>
              ) : canUpdateRentals ? (
                <Button type="button" variant="secondary" icon={Ban} onClick={openCancelModal}>
                  Cancelar contrato
                </Button>
              ) : (
                <p className="text-sm text-gray-500">
                  Solo quienes pueden editar rentas pueden registrar la cancelación desde aquí.
                </p>
              )}
            </div>
          </Card>

          {canUpdateRentals && (
            <Card title="Portal del inquilino">
              <div className="space-y-3">
                {tenant.accountUser ? (
                  <div className="space-y-1">
                    <p className="text-sm text-green-700 font-medium">Cuenta activa</p>
                    <p className="text-sm text-gray-600">{tenant.accountUser.name} · {tenant.accountUser.email}</p>
                  </div>
                ) : (
                  <p className="text-sm text-gray-600">
                    El inquilino aún no tiene acceso al portal. Al crear la cuenta se le enviará un correo con sus credenciales.
                  </p>
                )}
                {!tenant.accountUser && (
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={handleCreatePortalAccount}
                    disabled={portalAccountLoading}
                  >
                    {portalAccountLoading ? 'Creando cuenta…' : 'Crear cuenta de portal'}
                  </Button>
                )}
                {portalAccountMessage && (
                  <p className={`text-sm ${portalAccountMessage.startsWith('Error') ? 'text-red-600' : 'text-green-700'}`}>
                    {portalAccountMessage}
                  </p>
                )}
              </div>
            </Card>
          )}

          {canUpdateRentals && (
            <Card title="Administrador asignado">
              <div className="space-y-3">
                {tenant.assignedManager ? (
                  <p className="text-sm text-gray-700">
                    {tenant.assignedManager.name} · {tenant.assignedManager.email}
                  </p>
                ) : (
                  <p className="text-sm text-gray-500">Sin administrador asignado</p>
                )}
                <div className="flex gap-3 flex-wrap items-end">
                  <div className="flex-1 min-w-[200px]">
                    <label className="text-xs text-gray-500 mb-1 block">Cambiar administrador</label>
                    <select
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      value={assignManagerId}
                      onChange={(e) => setAssignManagerId(e.target.value)}
                    >
                      <option value="">Sin administrador</option>
                      {adminUsers.map((u) => (
                        <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
                      ))}
                    </select>
                  </div>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={handleAssignManager}
                    disabled={assignManagerLoading}
                  >
                    {assignManagerLoading ? 'Guardando…' : 'Asignar'}
                  </Button>
                </div>
              </div>
            </Card>
          )}

          <Card title="Documentos del contrato">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="text-sm text-gray-600 mb-2 block">Contrato</label>
                {tenant.contract ? (
                  tenant.contract.startsWith('data:application/pdf') ? (
                    <a
                      href={tenant.contract}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-accent hover:underline"
                    >
                      <FileText className="w-5 h-5" />
                      Ver documento PDF
                    </a>
                  ) : (
                    <img
                      src={tenant.contract}
                      alt="Contrato"
                      className="max-w-full h-auto max-h-64 rounded-lg border border-gray-200"
                    />
                  )
                ) : (
                  <p className="text-gray-500">No hay contrato adjunto</p>
                )}
              </div>
              <div>
                <label className="text-sm text-gray-600 mb-2 block">Identificación Oficial</label>
                {tenant.idOficial ? (
                  tenant.idOficial.startsWith('data:application/pdf') ? (
                    <a
                      href={tenant.idOficial}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-accent hover:underline"
                    >
                      <FileText className="w-5 h-5" />
                      Ver documento PDF
                    </a>
                  ) : (
                    <img
                      src={tenant.idOficial}
                      alt="Identificación Oficial"
                      className="max-w-full h-auto max-h-64 rounded-lg border border-gray-200"
                    />
                  )
                ) : (
                  <p className="text-gray-500">No hay identificación oficial adjunta</p>
                )}
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Tab: Información (formulario público de la solicitud) */}
      {activeTab === 'informacion' && (
        <div className="bg-white rounded-b-lg border border-t-0 border-gray-200 p-6">
          <Card title="Información del formulario">
            <div className="space-y-4 text-sm">
              {!tenant.prospectId ? (
                <p className="text-gray-500">
                  Este inquilino no está vinculado a un prospecto; no hay formulario público de solicitud que mostrar.
                </p>
              ) : !solicitudApplicationId ? (
                <p className="text-gray-500">
                  No se encontró la solicitud asociada a este prospecto. Si acabas de crear el inquilino, recarga la página.
                </p>
              ) : solicitudParsedNotes ? (
                <>
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                    {solicitudFormularioContestada && solicitudApplicationId && (
                      <Link
                        to={`/rentas/solicitudes/${solicitudApplicationId}`}
                        className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:text-primary/80"
                      >
                        <ExternalLink className="w-4 h-4 shrink-0" />
                        Ver en Solicitudes
                      </Link>
                    )}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <div className="text-gray-500">Tipo de persona</div>
                      <div className="font-medium text-gray-900">{solicitudParsedNotes?.tipoPersona || '—'}</div>
                    </div>
                    <div>
                      <div className="text-gray-500">Guardado</div>
                      <div className="font-medium text-gray-900">
                        {solicitudParsedNotes?.savedAt
                          ? formatDateTimeSpanishLong(String(solicitudParsedNotes.savedAt))
                          : '—'}
                      </div>
                    </div>
                  </div>

                  <div>
                    <div className="font-semibold text-gray-900 mb-2">Contacto</div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <div className="text-gray-500">Nombre</div>
                        <div className="font-medium text-gray-900">{solicitudParsedNotes?.contacto?.name || '—'}</div>
                      </div>
                      <div>
                        <div className="text-gray-500">Correo</div>
                        <div className="font-medium text-gray-900">{solicitudParsedNotes?.contacto?.email || '—'}</div>
                      </div>
                      <div>
                        <div className="text-gray-500">Teléfono</div>
                        <div className="font-medium text-gray-900">
                          {solicitudParsedNotes?.contacto?.phone
                            ? formatPhoneDisplay(String(solicitudParsedNotes.contacto.phone))
                            : '—'}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <div className="font-semibold text-gray-900 mb-2">Datos del formulario</div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {solicitudOrderedKeys.map((key) => {
                        if (!Object.prototype.hasOwnProperty.call(solicitudDatosFormulario, key)) return null;
                        const value = solicitudDatosFormulario[key];
                        const isEmpty =
                          value === null ||
                          value === undefined ||
                          (typeof value === 'string' && value.trim() === '');
                        if (isEmpty) return null;
                        return (
                          <div key={key} className="break-words">
                            <div className="text-gray-500">{labelizeKey(key)}</div>
                            <div className="font-medium text-gray-900">{formatDatoForField(key, value)}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </>
              ) : (
                <div>
                  <p className="text-gray-600 mb-3">
                    {solicitudFormularioContestada
                      ? 'No se pudo leer el formulario en el formato esperado.'
                      : 'El prospecto aún no envió el formulario público de solicitud. Cuando lo haga, la información aparecerá aquí.'}
                  </p>
                  {solicitudNotes && (
                    <>
                      <div className="text-gray-500 text-sm mb-2">Notas en la solicitud (texto interno)</div>
                      <pre className="whitespace-pre-wrap break-words text-sm bg-gray-50 border border-gray-200 rounded-lg p-3 max-h-96 overflow-auto">
                        {solicitudNotes}
                      </pre>
                    </>
                  )}
                </div>
              )}
            </div>
          </Card>
        </div>
      )}

      {/* Tab: Documentos */}
      {activeTab === 'documents' && (
        <div className="bg-white rounded-b-lg border border-t-0 border-gray-200 p-6 space-y-6">
          {/* Documentos del contrato (siempre visibles si existen) */}
          <Card title="Documentos del contrato">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="text-sm text-gray-600 mb-2 block">Contrato</label>
                {tenant.contract ? (
                  tenant.contract.startsWith('data:application/pdf') ? (
                    <a
                      href={tenant.contract}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-accent hover:underline"
                    >
                      <FileText className="w-5 h-5" />
                      Ver documento PDF
                    </a>
                  ) : (
                    <img
                      src={tenant.contract}
                      alt="Contrato"
                      className="max-w-full h-auto max-h-64 rounded-lg border border-gray-200 object-cover"
                    />
                  )
                ) : (
                  <p className="text-gray-500">No hay contrato adjunto</p>
                )}
              </div>
              <div>
                <label className="text-sm text-gray-600 mb-2 block">Identificación Oficial</label>
                {tenant.idOficial ? (
                  tenant.idOficial.startsWith('data:application/pdf') ? (
                    <a
                      href={tenant.idOficial}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-accent hover:underline"
                    >
                      <FileText className="w-5 h-5" />
                      Ver documento PDF
                    </a>
                  ) : (
                    <img
                      src={tenant.idOficial}
                      alt="Identificación Oficial"
                      className="max-w-full h-auto max-h-64 rounded-lg border border-gray-200 object-cover"
                    />
                  )
                ) : (
                  <p className="text-gray-500">No hay identificación oficial adjunta</p>
                )}
              </div>
            </div>
          </Card>

          {/* Documentos adicionales (expediente: modal agregar / editar inquilino) */}
          <Card title="Documentos adicionales">
            <p className="text-sm text-gray-600 mb-4">
              Los que cargas al <span className="font-medium">agregar</span> o <span className="font-medium">editar</span> inquilino en la sección «Documentos adicionales» del modal. Se guardan en el expediente del inquilino.
            </p>
            {tenantDocuments.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {tenantDocuments.map((doc) =>
                  doc.fileUrl ? (
                    <div key={doc.id} className="relative rounded-lg border border-gray-200 bg-white hover:border-primary/30 hover:shadow-sm transition-all">
                      {canDeleteRentals && (
                        <button
                          type="button"
                          onClick={() => handleRemoveDocument(doc.id)}
                          className="absolute top-2 right-2 z-10 p-2 rounded-lg text-red-600 bg-white/95 border border-gray-100 shadow-sm hover:bg-red-50 transition-colors"
                          title="Eliminar del expediente"
                          aria-label="Eliminar documento"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                      <DocumentViewer
                        title={doc.title}
                        fileUrl={doc.fileUrl}
                        fileTypeLabel={(doc.fileType || 'documento').toUpperCase()}
                        downloadName={doc.title}
                        className="p-4 border-0 rounded-lg shadow-none hover:shadow-none"
                      />
                    </div>
                  ) : null,
                )}
              </div>
            ) : (
              <div className="text-center py-8">
                <FileText className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-500 text-sm">No hay documentos adicionales en el expediente. Agrégalos desde <span className="font-medium">Editar</span>.</p>
              </div>
            )}
          </Card>

          {/* Documentos enviados en el formulario público de la solicitud */}
          <Card title="Documentos de la solicitud">
            <p className="text-sm text-gray-600 mb-4">
              Archivos que el prospecto adjuntó al enviar el <span className="font-medium">formulario público</span> de la solicitud. Son distintos de los documentos adicionales del expediente.
            </p>
            {solicitudApplicationId && (
              <div className="mb-4">
                <Link
                  to={`/rentas/solicitudes/${solicitudApplicationId}`}
                  className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:text-primary/80"
                >
                  <ExternalLink className="w-4 h-4 shrink-0" />
                  Abrir solicitud completa
                </Link>
              </div>
            )}
            {!tenant.prospectId ? (
              <p className="text-sm text-gray-500 py-6 text-center">
                Este inquilino no está vinculado a un prospecto; no hay solicitud de origen que mostrar.
              </p>
            ) : solicitudDocKeysWithValue.length === 0 ? (
              <div className="text-center py-10">
                <FileText className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-500 text-sm">
                  No hay archivos adjuntos en la solicitud, o el prospecto aún no envió el formulario público.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {solicitudDocKeysWithValue.map((key) => {
                  const value = solicitudDatosFormulario[key];
                  const viewUrl = getDocDownloadUrl(value);
                  const displayName = getDocDisplayName(key, value);
                  const mime = getDocMime(value);
                  const isPdf = isDocPdf(value);
                  const isImage = isDocImage(value);
                  const kindLabel = isImage ? 'IMAGEN' : isPdf ? 'PDF' : mime ? mime.split('/').pop()?.toUpperCase() || 'DOC' : 'DOC';
                  const displayDate = solicitudParsedNotes?.savedAt
                    ? formatDateSpanishLong(new Date(solicitudParsedNotes.savedAt))
                    : tenant.startDate
                      ? formatDateSpanishLong(new Date(tenant.startDate))
                      : '—';
                  if (!viewUrl) return null;
                  return (
                    <DocumentViewer
                      key={key}
                      title={displayName || labelizeKey(key)}
                      fileUrl={viewUrl}
                      fileTypeLabel={`${kindLabel} · Solicitud · ${displayDate}`}
                      downloadName={displayName || undefined}
                    />
                  );
                })}
              </div>
            )}
          </Card>
        </div>
      )}

      {/* Tab: Pagos */}
      {activeTab === 'payments' && (
        <TenantPaymentsTab
          tenantId={tenant.id}
          canUpdateRentals={canUpdateRentals}
          setToast={setToast}
          onPaymentRecorded={loadData}
        />
      )}

      {/* Tab: Checklist de entrega */}
      {activeTab === 'delivery-checklist' && (
        <div className="bg-white rounded-b-lg border border-t-0 border-gray-200 p-6">
          {!tenant.deliveryChecklistDone ? (
            <div className="space-y-4">
              {canManualCompleteChecklist && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                  <p className="text-sm text-amber-900 font-medium">
                    Opción manual (Administración)
                  </p>
                  <p className="text-xs text-amber-800 mt-1">
                    Si ya revisaron el documento/entrega sin pasar por el proceso del técnico,
                    pueden marcar el checklist como completado.
                  </p>
                  <div className="mt-3">
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => setManualChecklistOpen(true)}
                    >
                      Marcar checklist como completado
                    </Button>
                  </div>
                </div>
              )}

              <DeliveryInspectionSection tenantId={tenant.id} propertyId={tenant.propertyId} />
            </div>
          ) : (
            <DeliveryInspectionResults tenantId={tenant.id} />
          )}
        </div>
      )}

      {/* Tab: Historial */}
      {activeTab === 'history' && (
        <div className="bg-white rounded-b-lg border border-t-0 border-gray-200 p-6">
          <Card title="Historial">
            <HistoryTable rows={historyRows} loading={historyLoading} />
          </Card>
        </div>
      )}

      {/* Tab: Propiedad */}
      {activeTab === 'property' && (
        <div className="bg-white rounded-b-lg border border-t-0 border-gray-200 p-6">
          <Card>
            <div className="flex flex-col md:flex-row gap-6">
              {/* Foto de la propiedad */}
              <div className="md:w-80 shrink-0">
                <div className="aspect-video rounded-xl overflow-hidden bg-gray-100 border border-gray-200">
                  {propertyCoverImage ? (
                    <img
                      src={getPropertyCoverImageUrl(propertyCoverImage)}
                      alt={propertyName || 'Propiedad'}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                      <ImageIcon className="w-16 h-16" />
                    </div>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-2 text-center">Foto de la propiedad</p>
              </div>
              {/* Datos */}
              <div className="flex-1 space-y-4 min-w-0">
                <div>
                  <label className="text-sm text-gray-600">Propiedad</label>
                  <p className="text-lg font-semibold text-gray-900">{propertyName || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-600">Unidad</label>
                  <p className="text-base font-medium">{tenant.unitNumber}</p>
                </div>
                {unit && (
                  <>
                    <div>
                      <label className="text-sm text-gray-600">Metros Cuadrados</label>
                      <p className="text-base font-medium">{unit.m2} m²</p>
                    </div>
                    {unit.bedrooms != null && unit.bathrooms != null && (
                      <div>
                        <label className="text-sm text-gray-600">Características</label>
                        <p className="text-base font-medium">
                          {unit.bedrooms} habitaciones, {unit.bathrooms} baños
                        </p>
                      </div>
                    )}
                  </>
                )}
                <div className="pt-2 border-t border-gray-100">
                  <label className="text-sm text-gray-600">Renta Mensual</label>
                  <p className="text-2xl font-bold text-primary">
                    {canViewPrices ? `$${tenant.monthlyRent.toLocaleString('es-MX')}` : '—'}
                  </p>
                </div>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Modal para Editar */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={handleCloseEditModal}
        title="Editar Información del Contrato"
        size="lg"
      >
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Valor del Contrato (Renta Mensual) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Valor del Contrato (Renta Mensual) <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 font-medium">
                  $
                </span>
                <input
                  type="text"
                  value={monthlyRentDisplay}
                  onChange={(e) => {
                    const value = formatCurrencyInput(e.target.value);
                    setMonthlyRentDisplay(value);
                    setFormData((prev) => ({ ...prev, monthlyRent: parseCurrencyToNumber(value) }));
                  }}
                  onBlur={() => {
                    if (formData.monthlyRent > 0) {
                      setMonthlyRentDisplay(formData.monthlyRent.toLocaleString('es-MX', { minimumFractionDigits: 0, maximumFractionDigits: 2 }));
                    }
                  }}
                  onFocus={() => {
                    if (formData.monthlyRent > 0) {
                      setMonthlyRentDisplay(String(formData.monthlyRent));
                    }
                  }}
                  className="w-full pl-8 pr-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Ej: 15000 o 15000.50"
                />
              </div>
            </div>

            {/* Duración del Contrato (calculada) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Duración del Contrato
              </label>
              <div className="px-3 py-2 bg-gray-50 text-gray-700 border border-gray-300 rounded-lg">
                {formData.startDate && formData.endDate && formContractDuration > 0
                  ? `${formContractDuration} ${formContractDuration === 1 ? 'mes' : 'meses'}`
                  : 'Se calculará automáticamente'}
              </div>
            </div>

            {/* Estado del Contrato */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Estado del Contrato <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="active">Activo</option>
                <option value="terminado">Terminado</option>
                <option value="cancelado">Cancelado</option>
              </select>
            </div>

            {/* Fecha de Inicio */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fecha de Inicio del Contrato <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={formData.startDate}
                onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            {/* Fecha de Fin */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fecha de Fin del Contrato <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={formData.endDate}
                onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

          </div>

          {/* Contrato */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Contrato
            </label>
            <ImageUpload
              currentImage={formData.contract}
              onImageChange={(imageUrl) => setFormData((prev) => ({ ...prev, contract: imageUrl }))}
              label="Subir Contrato"
              acceptPdf
            />
          </div>

          {/* Identificación Oficial */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Identificación Oficial
            </label>
            <ImageUpload
              currentImage={formData.idOficial}
              onImageChange={(imageUrl) => setFormData((prev) => ({ ...prev, idOficial: imageUrl }))}
              label="Subir Identificación Oficial"
              acceptPdf
            />
          </div>

          {/* Documentos adicionales (misma sección que en «Agregar inquilino») */}
          <div className="border-t border-gray-200 pt-6">
            <h4 className="text-sm font-semibold text-gray-800 mb-1">Documentos adicionales</h4>
            <p className="text-xs text-gray-500 mb-3">
              Expediente del inquilino. No incluye los archivos del formulario público de la solicitud (esos se ven en la pestaña Documentos del inquilino).
            </p>
            {tenantDocuments.length > 0 && (
              <div className="space-y-2 mb-4">
                {tenantDocuments.map((doc) => (
                  <div key={doc.id} className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
                    <span className="text-sm text-gray-800">{doc.title}</span>
                    {canDeleteRentals && (
                      <button
                        type="button"
                        onClick={() => handleRemoveDocument(doc.id)}
                        className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                        title="Eliminar documento"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
            <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
              <h4 className="text-sm font-medium text-gray-700 mb-3">Agregar Nuevo Documento</h4>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Título del Documento</label>
                  <input
                    type="text"
                    value={newDocument.title}
                    onChange={(e) => setNewDocument((prev) => ({ ...prev, title: e.target.value }))}
                    className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary placeholder-gray-400"
                    placeholder="Ej: Título de Propiedad"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Archivo</label>
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    disabled={documentUploading}
                    onChange={handleDocumentFileSelect}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-accent file:text-white hover:file:bg-accent/90 disabled:opacity-50"
                  />
                  {documentUploading && (
                    <div className="mt-2 h-1.5 w-full bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary rounded-full transition-[width] duration-200 ease-out"
                        style={{ width: `${documentUploadProgress}%` }}
                      />
                    </div>
                  )}
                  {newDocument.fileUrl && !documentUploading && (
                    <div className="mt-2 flex items-center gap-2 p-2 rounded-lg bg-green-50 border border-green-200">
                      {newDocument.fileType?.toLowerCase() === 'pdf' ? (
                        <FileText className="w-8 h-8 text-red-600 shrink-0" />
                      ) : (
                        <ImageIcon className="w-8 h-8 text-blue-600 shrink-0" />
                      )}
                      <span className="text-sm text-green-700">Archivo listo. Escribe el título y pulsa &quot;Agregar Documento&quot;.</span>
                    </div>
                  )}
                </div>
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={handleAddDocument}
                    disabled={!newDocument.title.trim() || !newDocument.fileUrl}
                    className="px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 disabled:cursor-not-allowed enabled:bg-green-600 enabled:text-white enabled:hover:bg-green-700 disabled:bg-gray-400 disabled:text-gray-200"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Agregar Documento</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Botones */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={handleCloseEditModal}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={isSubmitting}
              className="px-4 py-2 bg-accent text-white rounded-lg hover:bg-accent/90 transition-colors flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="w-4 h-4" />
              <span>{isSubmitting ? 'Guardando...' : 'Guardar Cambios'}</span>
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isRenewModalOpen}
        onClose={() => !renewSubmitting && setIsRenewModalOpen(false)}
        title="Renovar contrato"
        size="md"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Indica la nueva fecha de fin (debe ser posterior a {formatDateLong(tenant.endDate)}).
          </p>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nueva fecha de fin <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              min={addDaysToIsoDate(tenant.endDate, 1)}
              value={renewForm.newEndDate}
              onChange={(e) => setRenewForm((prev) => ({ ...prev, newEndDate: e.target.value }))}
              disabled={renewSubmitting}
              className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
            />
          </div>
          {canViewPrices && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nueva renta mensual (opcional)</label>
              <p className="text-xs text-gray-500 mb-1">Si la dejas igual al valor actual, no se modifica el importe.</p>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 font-medium">$</span>
                <input
                  type="text"
                  value={renewForm.newMonthlyRentDisplay}
                  onChange={(e) =>
                    setRenewForm((prev) => ({ ...prev, newMonthlyRentDisplay: formatCurrencyInput(e.target.value) }))
                  }
                  disabled={renewSubmitting}
                  className="w-full pl-8 pr-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
                  placeholder="Mismo que actual si no cambia"
                />
              </div>
            </div>
          )}
          <div className="flex justify-end gap-3 pt-2 border-t border-gray-200">
            <button
              type="button"
              onClick={() => setIsRenewModalOpen(false)}
              disabled={renewSubmitting}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cerrar
            </button>
            <button
              type="button"
              onClick={handleRenewContractSubmit}
              disabled={renewSubmitting}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              <CalendarClock className="w-4 h-4" />
              {renewSubmitting ? 'Guardando...' : 'Confirmar renovación'}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isCancelModalOpen}
        onClose={() => !cancelSubmitting && setIsCancelModalOpen(false)}
        title="Cancelar contrato"
        size="md"
      >
        <div className="space-y-4">
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
            <p className="text-sm font-medium text-amber-900">¿Estás seguro de cancelar este contrato?</p>
            <p className="text-xs text-amber-800 mt-1">
              Esta acción marcará al inquilino como <strong>cancelado</strong> y liberará la unidad. No se puede deshacer desde aquí.
            </p>
          </div>
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={cancelForm.confirm}
              onChange={(e) => setCancelForm((prev) => ({ ...prev, confirm: e.target.checked }))}
              disabled={cancelSubmitting}
              className="mt-1 rounded border-gray-300 text-primary focus:ring-primary"
            />
            <span className="text-sm text-gray-800">Sí, estoy seguro de cancelar el contrato</span>
          </label>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Fecha de cancelación <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={cancelForm.cancellationDate}
              onChange={(e) => setCancelForm((prev) => ({ ...prev, cancellationDate: e.target.value }))}
              disabled={cancelSubmitting}
              className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Razón <span className="text-red-500">*</span>
            </label>
            <textarea
              value={cancelForm.reason}
              onChange={(e) => setCancelForm((prev) => ({ ...prev, reason: e.target.value }))}
              disabled={cancelSubmitting}
              rows={3}
              className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
              placeholder="Describe el motivo de la cancelación"
            />
          </div>
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={cancelForm.penaltyWaivedInternal}
              onChange={(e) =>
                setCancelForm((prev) => ({
                  ...prev,
                  penaltyWaivedInternal: e.target.checked,
                  penaltyAmountDisplay: e.target.checked ? '' : prev.penaltyAmountDisplay,
                }))
              }
              disabled={cancelSubmitting}
              className="mt-1 rounded border-gray-300 text-primary focus:ring-primary"
            />
            <span className="text-sm text-gray-800">Sin penalidad por razones internas</span>
          </label>
          {!cancelForm.penaltyWaivedInternal && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Monto de penalidad cobrada <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 font-medium">$</span>
                <input
                  type="text"
                  value={cancelForm.penaltyAmountDisplay}
                  onChange={(e) =>
                    setCancelForm((prev) => ({ ...prev, penaltyAmountDisplay: formatCurrencyInput(e.target.value) }))
                  }
                  disabled={cancelSubmitting}
                  className="w-full pl-8 pr-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
                  placeholder="Ej: 5000"
                />
              </div>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Inspección de salida (opcional)
            </label>
            <select
              value={cancelForm.assignedUserId}
              onChange={(e) => setCancelForm((prev) => ({ ...prev, assignedUserId: e.target.value }))}
              disabled={cancelSubmitting}
              className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50 text-sm"
            >
              <option value="">Sin inspección de salida</option>
              {maintenanceUsers.map((u: { id: string; name: string }) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Si eliges un técnico, se creará una inspección de salida tras confirmar la cancelación.
            </p>
          </div>
          <div className="flex justify-end gap-3 pt-2 border-t border-gray-200">
            <button
              type="button"
              onClick={() => setIsCancelModalOpen(false)}
              disabled={cancelSubmitting}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cerrar
            </button>
            <button
              type="button"
              onClick={handleCancelContractSubmit}
              disabled={cancelSubmitting || !cancelForm.confirm}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              <Ban className="w-4 h-4" />
              {cancelSubmitting ? 'Cancelando...' : 'Confirmar cancelación'}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isRevertModalOpen}
        onClose={() => !revertSubmitting && setIsRevertModalOpen(false)}
        title={tenant.prospectId ? 'Regresar a prospecto' : 'Eliminar inquilino'}
        size="md"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-700">
            {tenant.prospectId ? (
              <>
                ¿Estás seguro de regresar a <span className="font-semibold">{tenant.name}</span> como prospecto? Se
                eliminará su contrato, cuenta de acceso y datos de inquilino.
              </>
            ) : (
              <>
                ¿Estás seguro de eliminar al inquilino <span className="font-semibold">{tenant.name}</span>? Se eliminará
                su contrato, cuenta de acceso y todos sus datos.
              </>
            )}
          </p>
          {revertError && (
            <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm">{revertError}</div>
          )}
          <div className="flex justify-end gap-3 pt-2 border-t border-gray-200">
            <button
              type="button"
              onClick={() => setIsRevertModalOpen(false)}
              disabled={revertSubmitting}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleRevertConfirm}
              disabled={revertSubmitting}
              className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-50 inline-flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              {revertSubmitting ? 'Procesando...' : 'Confirmar'}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={manualChecklistOpen}
        onClose={() => !manualChecklistSubmitting && setManualChecklistOpen(false)}
        title="Confirmar checklist manual"
        size="md"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-700">
            Esto marcará <strong>“Checklist completado por técnico”</strong> como completado de forma manual.
          </p>
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
            Úsalo solo si ya validaron la entrega por su cuenta.
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-gray-200">
            <Button
              type="button"
              variant="secondary"
              disabled={manualChecklistSubmitting}
              onClick={() => setManualChecklistOpen(false)}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              disabled={manualChecklistSubmitting}
              onClick={async () => {
                if (!tenant) return;
                if (!canManualCompleteChecklist) {
                  setToast({
                    message:
                      'No tienes permisos para completar el checklist manualmente.',
                    type: 'error',
                  });
                  return;
                }
                setManualChecklistSubmitting(true);
                try {
                  await api.completeTenantDeliveryChecklistManually(
                    tenant.id,
                    'Marcado manualmente por administración desde el detalle del inquilino.',
                  );
                  setToast({ message: 'Checklist marcado como completado.', type: 'success' });
                  setManualChecklistOpen(false);
                  await loadData();
                } catch (e: any) {
                  setToast({
                    message: e?.message || 'No se pudo marcar el checklist.',
                    type: 'error',
                  });
                } finally {
                  setManualChecklistSubmitting(false);
                }
              }}
            >
              {manualChecklistSubmitting ? 'Confirmando...' : 'Confirmar'}
            </Button>
          </div>
        </div>
      </Modal>

      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}
    </div>
  );
};

